// Pure helpers for the Search page: @mentions in the one-line query, the "Understood as" preview,
// and grouping /search/nlq results by video and scene.

const rx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const NOT_WORD = "(?![\\p{L}\\p{N}_])";

// Words after a mention that make the person the speaker ("@Ann asks…"), English and French
export const SPEECH_VERBS = new Set(
  (
    "say says said saying talk talks talking talked mention mentions mentioned mentioning ask asks asked asking " +
    "speak speaks spoke speaking discuss discusses discussed discussing explain explains explained explaining " +
    "announce announces announced tell tells told telling answer answers answered answering argue argues argued " +
    "arguing shout shouts shouted yell yells yelled whisper whispers whispered reply replies replied " +
    "dit disent dire parle parlent parler demande demandent demander explique expliquent annonce annoncent " +
    "répond répondent crie crient raconte racontent"
  ).split(" "),
);

/** "00:06:40" -> 400 */
export function parseDuration(d) {
  return (d || "").split(":").reduce((s, part) => s * 60 + Number(part), 0) || 0;
}

/** [{start, end, name}] of the "@Name" mentions of known names in text, longest names first */
export function findMentions(text, names) {
  if (!text || !names.length) return [];
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const re = new RegExp("(?<!\\S)@(" + sorted.map(rx).join("|") + ")" + NOT_WORD, "giu");
  const out = [];
  let m;
  while ((m = re.exec(text))) {
    const found = m[1].toLowerCase();
    out.push({ start: m.index, end: m.index + m[0].length, name: sorted.find((n) => n.toLowerCase() === found) });
  }
  return out;
}

/** the "@query" typed just before the caret, {start, end, q}, or null */
export function atQuery(text, caret) {
  const at = text.lastIndexOf("@", caret - 1);
  if (at < 0 || (at > 0 && !/\s/.test(text[at - 1]))) return null;
  const q = text.slice(at + 1, caret);
  if (q.length > 30 || /[@"“”\n]/.test(q)) return null;
  return { start: at, end: caret, q };
}

/** people [{name, videos}] whose name, or a word of it, starts with q; with the matched part for bolding */
export function matchPeople(people, q) {
  const lo = q.toLowerCase();
  const out = [];
  people.forEach((p) => {
    const name = p.name.toLowerCase();
    let at = name.startsWith(lo) ? 0 : -1;
    for (let i = name.indexOf(" "); at < 0 && i >= 0; i = name.indexOf(" ", i + 1)) {
      if (name.startsWith(lo, i + 1)) at = i + 1;
    }
    if (at >= 0) out.push({ ...p, at, len: q.length });
  });
  return out;
}

/** text with "@query" (start..end) replaced by "@Name ", and the caret position after it */
export function insertMention(text, at, name) {
  const before = text.slice(0, at.start) + "@" + name + " ";
  return { text: before + text.slice(at.end).replace(/^\s+/, ""), caret: before.length };
}

/** what the query will search for, guessed on the client while typing: [{kind, value}] */
export function previewConcepts(text, names) {
  const out = [];
  let rest = text || "";
  findMentions(rest, names).forEach((m) => {
    const next = rest.slice(m.end).toLowerCase().match(/[\p{L}'’]+/gu) || [];
    const speaking = next.slice(0, 2).some((w) => SPEECH_VERBS.has(w));
    if (!out.some((c) => c.value === m.name)) out.push({ kind: speaking ? "speaker" : "character", value: m.name });
  });
  findMentions(rest, names)
    .reverse()
    .forEach((m) => (rest = rest.slice(0, m.start) + " " + rest.slice(m.end)));
  rest = rest.replace(/["“]([^"”]*)["”]?/g, (q, phrase) => {
    if (phrase.trim()) out.push({ kind: "phrase", value: phrase.trim() });
    return " ";
  });
  // the rest is searched as a whole, as the API does
  rest = rest.replace(/\s+/g, " ").trim();
  if (rest) out.push({ kind: "about", value: rest });
  return out;
}

const JOINERS = new Set(["and", "et", "&", "+"]);

/**
 * Whether /search/nlq answers the query without Gemini, as the API decides: only one or two @mentions
 * ("@Ann and @Bob"), or only "exact text" in quotes.
 */
export function isQuickSearch(text, names) {
  const q = (text || "").trim();
  if (/^["“][^"“”]+["”]$/.test(q)) return !!q.slice(1, -1).trim();
  const mentions = findMentions(q, names);
  const people = new Set(mentions.map((m) => m.name));
  if (people.size < 1 || people.size > 2) return false;
  let rest = q;
  [...mentions].reverse().forEach((m) => (rest = rest.slice(0, m.start) + " " + rest.slice(m.end)));
  return rest
    .split(/[\s,]+/)
    .filter(Boolean)
    .every((w) => JOINERS.has(w.toLowerCase()));
}

/** estimated seconds a search for text takes: Gemini takes about 5 */
export const searchEstimate = (text, names) => (isQuickSearch(text, names) ? 1 : 5);

/** split text into [{t, hit}] around words starting with any of terms ("budget" also marks "budgets") */
export function highlightParts(text, terms) {
  const s = text || "";
  const words = [...new Set((terms || []).map((w) => w.trim()).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!words.length) return [{ t: s, hit: false }];
  const re = new RegExp("(?<![\\p{L}\\p{N}_])(?:" + words.map((w) => w.split(/\s+/).map(rx).join("\\s+")).join("|") + ")[\\p{L}\\p{N}_]*", "giu");
  const out = [];
  let k = 0,
    m;
  while ((m = re.exec(s))) {
    if (m.index > k) out.push({ t: s.slice(k, m.index), hit: false });
    out.push({ t: m[0], hit: true });
    k = m.index + m[0].length;
  }
  if (k < s.length || !out.length) out.push({ t: s.slice(k), hit: false });
  return out;
}

/** split text into [{t, hit}] around the matched [start, end) spans, counted in code points as the API does */
export function spanParts(text, spans) {
  const chars = Array.from(text || "");
  const out = [];
  let k = 0;
  [...spans].sort((a, b) => a[0] - b[0]).forEach(([a, b]) => {
    if (a < k) return;
    if (a > k) out.push({ t: chars.slice(k, a).join(""), hit: false });
    out.push({ t: chars.slice(a, b).join(""), hit: true });
    k = b;
  });
  if (k < chars.length || !out.length) out.push({ t: chars.slice(k).join(""), hit: false });
  return out;
}

/** the evidence kinds of a result: speech (said lines), moment (scene events: what happens), person (someone on screen) */
export function resultTags(r) {
  const tags = [];
  if (r.lines.length && (r.lines.some((l) => l.hit) || !r.events.length)) tags.push("speech");
  if (r.events.length) tags.push("moment");
  if (r.on_screen.length) tags.push("person");
  return tags;
}

/** what a result row shows: the hit line, else the scene event, or who is on screen */
export function primaryOf(r) {
  if (r.type === "on_screen") return { kind: "screen", names: r.people, duration: r.end - r.start };
  const line = r.lines.find((l) => l.hit) || (!r.events.length && r.lines[0]);
  return line ? { kind: "line", line } : { kind: "event", event: r.events[0] };
}

/** the language /search/nlq writes its reasons in, for the UI language (e.g. "fr-CA") */
export const searchLang = (language) => (String(language || "").slice(0, 2) === "fr" ? "fr" : "en");

/**
 * results with their text in language (e.g. "fr-CA"): /search/nlq gives the translated text of a field next to it as
 * <field>_<lang> (scene title_fr, events text_fr, lines speaker_fr); the original text is kept when there is none.
 * The reasons stay in the language of the search, transcript lines in the language of the video.
 */
export function localizeResults(results, language) {
  const suffix = "_" + String(language || "").slice(0, 2);
  const text = (o, key) => o[key + suffix] || o[key];
  return results.map((r) => ({
    ...r,
    scene: { ...r.scene, title: text(r.scene, "title") },
    lines: r.lines.map((l) => ({ ...l, speaker: text(l, "speaker") })),
    events: r.events.map((e) => ({ ...e, text: text(e, "text") })),
  }));
}

const byName = (videos) => (a, b) =>
  (videos[a.projectId]?.project_name || "").localeCompare(videos[b.projectId]?.project_name || "", undefined, { numeric: true }) ||
  a.projectId - b.projectId;

/**
 * Group results (best first, as returned by /search/nlq) by video and scene.
 * sort "relevance" keeps the best moments first; "order" lists videos by name and moments in time order.
 * Only matches are kept unless showRelated.
 * Returns {groups: [{projectId, scenes: [{scene, rows}]}], order: [ids in display order], counts, relatedCount}
 */
export function groupResults(results, videos, { sort = "relevance", showRelated = false } = {}) {
  const relatedCount = results.filter((r) => r.label !== "match").length;
  const rank = new Map(results.map((r, i) => [r.id, i]));
  const shown = showRelated ? results : results.filter((r) => r.label === "match");
  const byVideo = new Map();
  shown.forEach((r) => {
    if (!byVideo.has(r.project_id)) byVideo.set(r.project_id, new Map());
    const scenes = byVideo.get(r.project_id);
    if (!scenes.has(r.scene.index)) scenes.set(r.scene.index, { scene: r.scene, rows: [] });
    scenes.get(r.scene.index).rows.push(r);
  });
  const best = (rows) => Math.min(...rows.map((r) => rank.get(r.id)));
  const groups = [...byVideo.entries()].map(([projectId, scenes]) => {
    const list = [...scenes.values()];
    list.forEach((s) => s.rows.sort(sort === "order" ? (a, b) => a.start - b.start : (a, b) => rank.get(a.id) - rank.get(b.id)));
    list.sort(sort === "order" ? (a, b) => a.scene.index - b.scene.index : (a, b) => best(a.rows) - best(b.rows));
    return { projectId, scenes: list, best: best(list.flatMap((s) => s.rows)) };
  });
  groups.sort(sort === "order" ? byName(videos) : (a, b) => a.best - b.best);
  const order = groups.flatMap((g) => g.scenes.flatMap((s) => s.rows.map((r) => r.id)));
  // scene index -1: moments in a part of a video without indexed scenes
  const scenes = groups.reduce((n, g) => n + g.scenes.filter((s) => s.scene.index >= 0).length, 0);
  const counts = { moments: order.length, scenes, videos: groups.length };
  return { groups, order, counts, relatedCount };
}
